import type { EmployeeQualificationsResponse, SourceTypeCode } from '../../api/types';
import { VERIFICATION_COPY, formatCalendarDate } from './factDisplay';
import { EditQualificationControl } from './EmployeeFactsCapture';
import styles from './FactRecords.module.css';

/**
 * The Qualifications section (docs/DOMAIN_MODEL.md §2.9).
 *
 * ---------------------------------------------------------------------
 * WHAT THIS SECTION REFUSES TO DECIDE
 *
 * Three decisions are available here, all tempting, and all belong to a
 * person rather than to this component:
 *
 * 1. **Whether a credential is valid.** The API returns `expired` and
 *    `verificationStatus` as two separate fields and never merges them
 *    into one "valid" flag, because an unexpired-but-unverified record and
 *    an expired-but-verified one are genuinely different situations. They
 *    are rendered as two separate facts for the same reason. `expired` is
 *    shown as what it is — `expiresOn` compared against the date the
 *    request was answered at — with the date beside it, so the comparison
 *    is checkable rather than taken on trust.
 *
 * 2. **Whether an expired record still matters.** It is never hidden or
 *    filtered. A lapsed qualification is a real, retained fact about
 *    someone's history (docs/DOMAIN_MODEL.md §11 — facts are not deleted),
 *    and dropping it would quietly rewrite that history. It is marked
 *    clearly so it is also never mistaken for a current one.
 *
 * 3. **Which record counts, when there are several.** Re-certification
 *    produces multiple records for one qualification, and every one is
 *    listed. No record is promoted to "the current one", none is discarded
 *    as superseded, and no most-recent-wins rule is applied. Role Fit does
 *    pick the latest for its own threshold comparison — that is a Role Fit
 *    concern with its own documented rule, not a presentation default this
 *    section is entitled to borrow. Where a qualification has more than one
 *    record, every one of them says so, so the pattern is visible rather
 *    than something the reader has to notice by scanning names.
 * ---------------------------------------------------------------------
 */
export function QualificationsSection({
  data,
  employeeId,
  viewerCanAssessAs,
  onChanged,
}: {
  data: EmployeeQualificationsResponse;
  employeeId: string;
  viewerCanAssessAs: SourceTypeCode | null;
  onChanged: () => void;
}) {
  // How many records exist per qualification. A count of the rows the API
  // returned — it selects nothing, ranks nothing and discards nothing.
  const recordsPerQualification = new Map<string, number>();
  for (const entry of data.qualifications) {
    recordsPerQualification.set(
      entry.qualification.id,
      (recordsPerQualification.get(entry.qualification.id) ?? 0) + 1
    );
  }

  return (
    <ul className={styles.list}>
      {data.qualifications.map((entry) => {
        const verification = VERIFICATION_COPY[entry.verificationStatus];
        const siblingCount = recordsPerQualification.get(entry.qualification.id) ?? 1;

        return (
          <li key={entry.id} className={styles.record}>
            <div className={styles.primary}>
              <span className={styles.name}>{entry.qualification.name}</span>
              {entry.qualification.category ? (
                <span className={styles.category}>{entry.qualification.category}</span>
              ) : null}

              {entry.expired ? (
                <span
                  className={`${styles.marker} ${styles.markerAttention}`}
                  title="The recorded expiry date has passed. Whether that matters is a judgement for a person to make."
                >
                  Expired
                </span>
              ) : null}

              <span className={styles.level} title={verification.meaning}>
                {verification.label}
              </span>

              {siblingCount > 1 ? (
                <span
                  className={styles.marker}
                  title="This person holds more than one record for this qualification — a re-certification history. Every record is listed; none is treated as replacing another."
                >
                  {siblingCount} records held for this qualification
                </span>
              ) : null}

              {entry.qualification.status === 'deprecated' ? (
                <span
                  className={styles.marker}
                  title="This qualification has been retired from the organisation's library. The record is still held."
                >
                  Retired from the qualification library
                </span>
              ) : null}
            </div>

            <dl className={styles.facts}>
              <div>
                <dt>Issued by</dt>
                <dd>
                  {entry.issuingBody ?? <span className={styles.absent}>Not recorded</span>}
                </dd>
              </div>
              <div>
                <dt>Obtained</dt>
                <dd>
                  {entry.obtainedOn === null ? (
                    <span className={styles.absent}>Not recorded</span>
                  ) : (
                    formatCalendarDate(entry.obtainedOn)
                  )}
                </dd>
              </div>
              <div>
                <dt>Expiry</dt>
                <dd>
                  {entry.expiresOn === null ? (
                    <span className={styles.absent}>No expiry recorded</span>
                  ) : (
                    `${entry.expired ? 'Expired ' : 'Expires '}${formatCalendarDate(entry.expiresOn)}`
                  )}
                </dd>
              </div>
              <div>
                <dt>Verification</dt>
                <dd>{verification.meaning}</dd>
              </div>
            </dl>

            {viewerCanAssessAs !== null ? (
              <EditQualificationControl employeeId={employeeId} qualification={entry} onChanged={onChanged} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
